import React, { useState, useCallback } from 'react';
import { Icons } from '../shared/ui';
const { Bell, X, CheckCircle2: CheckCircle, AlertCircle, Info, Alert: AlertTriangle } = Icons;
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';

const NotificationBell = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { user } = useAuth();
  const {
    notifications,
    isConnected,
    markAsRead,
    clearNotification,
    clearAllNotifications,
    getUnreadCount
  } = useNotifications();

  const unreadCount = getUnreadCount();

  // Get icon based on notification type
  const getNotificationIcon = (notification) => {
    const iconClass = "w-4 h-4";

    switch (notification.type) {
      case 'payment':
        return <AlertCircle className={`${iconClass} text-green-600`} />;
      case 'ticket':
        return <AlertTriangle className={`${iconClass} text-yellow-600`} />;
      case 'contract':
        return <CheckCircle className={`${iconClass} text-teal-600`} />;
      case 'maintenance':
        return <AlertTriangle className={`${iconClass} text-orange-600`} />;
      case 'violation':
        return <AlertCircle className={`${iconClass} text-red-600`} />;
      default:
        return <Info className={`${iconClass} text-stone-600`} />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-l-4 border-red-500';
      case 'medium':
        return 'border-l-4 border-yellow-500';
      default:
        return 'border-l-4 border-teal-500';
    }
  };

  // Format time for display
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (e) {
      return '';
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate to action URL if provided
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
      setShowDropdown(false);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = useCallback(() => {
    clearAllNotifications();
  }, [clearAllNotifications]);

  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    setShowDropdown(prev => !prev);
  }, []);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.notification-dropdown')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  if (!user) {
    return null;
  }

  return (
    <div className="relative notification-dropdown">
      <button
        onClick={toggleDropdown}
        className="relative p-2 text-stone-600 hover:text-stone-900 transition-colors"
        title={isConnected ? 'Notifications' : 'Notifications (Offline)'}
      >
        <Bell size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {!isConnected && (
          <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full border-2 border-white"
                title="Disconnected from notification service" />
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl z-50 max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-lg">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-teal-600 hover:text-teal-800"
                >
                  Mark all as read
                </button>
              )}
              <button
                onClick={() => setShowDropdown(false)}
                className="p-1 hover:bg-stone-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Connection status */}
          {!isConnected && (
            <div className="px-4 py-2 bg-yellow-50 text-yellow-800 text-sm">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                <span>Offline - Reconnecting...</span>
              </div>
            </div>
          )}

          {/* Notifications list */}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-stone-500 text-center">
                <Bell className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p>No notifications yet</p>
                <p className="text-sm mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-stone-50 cursor-pointer transition-colors ${
                      getPriorityColor(notification.priority)
                    } ${!notification.read ? 'bg-teal-50' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {getNotificationIcon(notification)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium text-sm">
                            {notification.title || 'Notification'}
                          </h4>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-teal-600 rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <p className="text-sm text-stone-600 mt-1">
                          {notification.message}
                        </p>
                        {notification.actionUrl && (
                          <button className="text-sm text-teal-600 hover:text-teal-800 mt-2">
                            View Details →
                          </button>
                        )}
                        <p className="text-xs text-stone-400 mt-2">
                          {formatTime(notification.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t">
              <button
                className="w-full text-center text-sm text-teal-600 hover:text-teal-800"
                onClick={() => {
                  window.location.href = '/notifications';
                  setShowDropdown(false);
                }}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;